/**
 * PsicoSalud — Partículas del panel de login.
 *
 * Sistema de nodos blancos que se desplazan muy lentamente sobre el
 * fondo verde-teal del panel editorial. Cuando dos nodos se acercan
 * por debajo de cierta distancia se traza una línea semi-transparente
 * entre ellos, evocando una red neuronal suave que respira.
 *
 * Decisiones técnicas:
 * - Canvas 2D en lugar de WebGL: el efecto es geométrico simple
 *   (círculos + líneas), no necesita shaders. Cuesta una fracción
 *   del trabajo y no requiere extensión del navegador.
 * - requestAnimationFrame con tiempo delta para mantener velocidad
 *   estable aunque el equipo sufra throttling.
 * - prefers-reduced-motion: si está activo, dibuja un único frame
 *   estático y cancela el loop. El efecto sigue visible sin movimiento.
 * - DPR-aware: el canvas multiplica su resolución por devicePixelRatio
 *   para verse nítido en pantallas HiDPI.
 */

import { useEffect, useRef } from 'react';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

interface LoginParticlesProps {
  /** Cantidad de nodos (default 32). */
  count?: number;
  /** Distancia máxima (en px lógicos) para conectar dos nodos con línea. */
  linkDistance?: number;
}

export default function LoginParticles({
  count = 32,
  linkDistance = 140,
}: LoginParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const reducedMotion = useRef<boolean>(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    reducedMotion.current =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let dpr = window.devicePixelRatio || 1;
    let nodes: Node[] = [];

    function resize() {
      const rect = canvas!.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = window.devicePixelRatio || 1;
      canvas!.width = Math.round(width * dpr);
      canvas!.height = Math.round(height * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function init() {
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1.4 + Math.random() * 1.4,
      }));
    }

    function step(now: number, last: { t: number }) {
      const dt = Math.min(48, now - last.t) / 16.67; // normalizado a ~60fps
      last.t = now;

      ctx!.clearRect(0, 0, width, height);

      for (const n of nodes) {
        n.x += n.vx * dt;
        n.y += n.vy * dt;

        // Wrap en los bordes para que la "red" sea continua.
        if (n.x < -10) n.x = width + 10;
        else if (n.x > width + 10) n.x = -10;
        if (n.y < -10) n.y = height + 10;
        else if (n.y > height + 10) n.y = -10;
      }

      // Conexiones: para cada par cercano, línea con alpha según distancia.
      ctx!.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distSq = dx * dx + dy * dy;
          const limitSq = linkDistance * linkDistance;
          if (distSq < limitSq) {
            const dist = Math.sqrt(distSq);
            const alpha = (1 - dist / linkDistance) * 0.28;
            ctx!.strokeStyle = `rgba(255, 255, 255, ${alpha.toFixed(3)})`;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
          }
        }
      }

      // Nodos.
      ctx!.fillStyle = 'rgba(255, 255, 255, 0.78)';
      for (const n of nodes) {
        ctx!.beginPath();
        ctx!.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx!.fill();
      }

      if (!reducedMotion.current) {
        rafRef.current = requestAnimationFrame((t) => step(t, last));
      }
    }

    resize();
    init();

    if (reducedMotion.current) {
      // Dibuja un único frame estático.
      step(performance.now(), { t: performance.now() });
    } else {
      rafRef.current = requestAnimationFrame((t) => step(t, { t }));
    }

    const onResize = () => {
      resize();
      init();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [count, linkDistance]);

  return <canvas ref={canvasRef} className="login-particles" aria-hidden="true" />;
}
